import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    Select,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    VStack,
    Text,
    useToast,
    FormErrorMessage,
    Spinner,
} from '@chakra-ui/react';
import { format, addDays, isBefore, startOfDay, isValid } from 'date-fns';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

const AppointmentScheduler = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        service: '',
        date: '',
        time: '',
    });
    const [errors, setErrors] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const toast = useToast();

    const defaultTimeSlots = [
        '09:00', '10:00', '11:00', '12:00',
        '13:00', '14:00', '15:00', '16:00', '17:00',
    ];

    const getAvailableDates = () => {
        const dates = [];
        const today = startOfDay(new Date());
        for (let i = 0; i < 7; i++) {
            const date = addDays(today, i);
            if (!isBefore(date, today)) {
                dates.push(format(date, 'yyyy-MM-dd'));
            }
        }
        return dates;
    };

    useEffect(() => {
        if (formData.date && isValid(new Date(formData.date))) {
            setIsLoading(true);
            const fetchAvailability = async () => {
                try {
                    console.log('Fetching availability for date:', formData.date);
                    const appointmentsRef = collection(db, 'appointments');
                    const q = query(appointmentsRef, where('date', '==', formData.date));
                    const querySnapshot = await getDocs(q);
                    const bookedSlots = querySnapshot.empty
                        ? []
                        : querySnapshot.docs.map(doc => doc.data().time);
                    console.log('Booked slots:', bookedSlots);
                    const availableSlots = defaultTimeSlots.filter(
                        slot => !bookedSlots.includes(slot)
                    );
                    console.log('Available slots:', availableSlots);
                    setAvailableTimeSlots(availableSlots);
                    if (formData.time && !availableSlots.includes(formData.time)) {
                        setFormData({ ...formData, time: '' });
                    }
                } catch (error) {
                    console.error('Error fetching availability:', {
                        message: error.message,
                        code: error.code,
                        details: error.details,
                    });
                    toast({
                        title: 'Error',
                        description: `Failed to fetch availability: ${error.message} (${error.code})`,
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                } finally {
                    setIsLoading(false);
                }
            };
            fetchAvailability();
        }
    }, [formData.date]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name is required';
        if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/))
            newErrors.email = 'Invalid email format';
        if (!formData.service) newErrors.service = 'Please select a service';
        if (!formData.date) newErrors.date = 'Please select a date';
        else if (!isValid(new Date(formData.date))) newErrors.date = 'Invalid date';
        if (!formData.time) newErrors.time = 'Please select a time';
        return newErrors;
    };

    const handleBaseChange = (e) => {
        const updatedFormData = { ...formData, [e.target.name]: e.target.value };
        setFormData(updatedFormData);
        setErrors(validateForm());
    };

    const handleDateChange = (e) => {
        const updatedFormData = { ...formData, time: '', [e.target.name]: e.target.value };
        setFormData(updatedFormData);
        setErrors(validateForm());
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = validateForm();
        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0 && isValid(new Date(formData.date))) {
            setIsOpen(true);
        } else {
            toast({
                title: 'Error',
                description: 'Please fill out all fields correctly.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const confirmAppointment = async () => {
        if (!isValid(new Date(formData.date))) {
            toast({
                title: 'Error',
                description: 'Invalid date selected. Please try again.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            setIsOpen(false);
            return;
        }

        setIsLoading(true);
        try {
            const appointmentsRef = collection(db, 'appointments');
            await addDoc(appointmentsRef, {
                ...formData,
                createdAt: new Date().toISOString(),
                status: 'confirmed',
            });
            toast({
                title: 'Appointment Scheduled',
                description: `Your appointment for ${formData.service} on ${format(new Date(formData.date), 'MMMM d, yyyy')} at ${formData.time} has been scheduled!`,
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
            setIsOpen(false);
            setFormData({ name: '', email: '', service: '', date: '', time: '' });
            setAvailableTimeSlots([]);
        } catch (error) {
            console.error('Error saving appointment:', {
                message: error.message,
                code: error.code,
                details: error.details,
            });
            toast({
                title: 'Error',
                description: `Failed to schedule appointment: ${error.message} (${error.code})`,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const isFormValid = () => {
        const newErrors = validateForm();
        return Object.keys(newErrors).length === 0 && isValid(new Date(formData.date));
    };

    return (
        <Box
            maxW={{ base: '90%', md: '50%', lg: '50%' }}
            mx="auto"
            p={8}
            // bg="gray.100"
            id='AppointmentScheduler'
            borderRadius="lg"
            boxShadow="lg"
            opacity={"0.8"}
            mt={10}
            mb={10}
            border={"3px solid"}
            borderColor={"customDarkBlue.300"}
        >
            <VStack as="form" spacing={6} onSubmit={handleSubmit}>
                <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="customDarkBlue.600">
                    Schedule Your Appointment
                </Text>

                <FormControl isInvalid={errors.name} isRequired>
                    <FormLabel color="gray.700">Full Name</FormLabel>
                    <Input
                        name="name"
                        value={formData.name}
                        onChange={handleBaseChange}
                        placeholder="Enter your full name"
                        focusBorderColor="teal.500"
                        color={"gray.600"}
                    />
                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.email} isRequired>
                    <FormLabel color="gray.700">Email</FormLabel>
                    <Input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleBaseChange}
                        placeholder="Enter your email"
                        focusBorderColor="teal.500"
                        color={"gray.600"}
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.service} isRequired color={"gray.600"}>
                    <FormLabel color="gray.700">Service</FormLabel>
                    <Select
                        name="service"
                        value={formData.service}
                        onChange={handleBaseChange}
                        placeholder="Select a service"
                        focusBorderColor="teal.500"
                    >
                        <option value="Web Development">Web Development Consultation</option>
                        <option value="Mobile App Development">Mobile App Development Consultation</option>
                        <option value="AR/VR Consultation">AR/VR Consultation</option>
                        <option value="AI Development">AI Development Consultation</option>
                        <option value="SEO and Google Ads">SEO and Google Ads</option>
                        <option value="Social Media Marketing">Social Media Marketing</option>
                         <option value="Graphic Design Consultation">Graphic Design Consultation</option>
                    </Select>
                    <FormErrorMessage>{errors.service}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.date} isRequired color={"gray.600"}>
                    <FormLabel color="gray.700">Date</FormLabel>
                    <Select
                        name="date"
                        value={formData.date}
                        onChange={handleDateChange}
                        placeholder="Select a date"
                        focusBorderColor="teal.500"
                    >
                        {getAvailableDates().map((date) => (
                            <option key={date} value={date}>
                                {format(new Date(date), 'MMMM d, yyyy')}
                            </option>
                        ))}
                    </Select>
                    <FormErrorMessage>{errors.date}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={errors.time} isRequired color={"gray.600"}>
                    <FormLabel color="gray.700">Time</FormLabel>
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        <Select
                            name="time"
                            value={formData.time}
                            onChange={handleBaseChange}
                            placeholder="Select a time"
                            focusBorderColor="teal.500"
                            isDisabled={!formData.date || !isValid(new Date(formData.date))}
                        >
                            {availableTimeSlots.map((time) => (
                                <option key={time} value={time}>
                                    {time}
                                </option>
                            ))}
                        </Select>
                    )}
                    <FormErrorMessage>{errors.time}</FormErrorMessage>
                </FormControl>

                <Button
                    type="submit"
                    //   colorScheme="teal"
                    bg={"customDarkBlue.400"}
                    color="white"
                    width="full"
                    isLoading={isLoading}
                    isDisabled={!isFormValid()}
                    _hover={{ bg: 'customDarkBlue.600' }}
                >
                    Schedule Appointment
                </Button>
            </VStack>

            <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
                <ModalOverlay />
                <ModalContent bg={"gray.500"}>
                    <ModalHeader>Confirm Appointment</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {isValid(new Date(formData.date)) ? (
                            <Text>
                                Please confirm your appointment details:
                                <br />
                                <strong>Name:</strong> {formData.name}
                                <br />
                                <strong>Email:</strong> {formData.email}
                                <br />
                                <strong>Service:</strong> {formData.service}
                                <br />
                                <strong>Date:</strong> {format(new Date(formData.date), 'MMMM d, yyyy')}
                                <br />
                                <strong>Time:</strong> {formData.time}
                            </Text>
                        ) : (
                            <Text color="red.500">Invalid date selected. Please choose a valid date.</Text>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="ghost"
                            mr={3}
                            onClick={() => setIsOpen(false)}
                            isDisabled={isLoading}
                        >
                            Cancel
                        </Button>
                        <Button
                            colorScheme="teal"
                            onClick={confirmAppointment}
                            isLoading={isLoading}
                            isDisabled={!isValid(new Date(formData.date))}
                        >
                            Confirm
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default AppointmentScheduler;